import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Link2, CalendarClock, ArrowUpDown, X, Zap } from 'lucide-react';
import { getApiUrl } from '../../utils/api';
import { EmptyState, TableRowsSkeleton } from '../ds';

const API_URL = getApiUrl();

export const STATUS_OPTIONS = [
  { value: 'wip', label: 'WIP', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  { value: 'renewed', label: 'Renewed', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  { value: 'not_interested', label: 'Not Interested', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  { value: 'pricing_issue', label: 'Pricing Issue', cls: 'bg-rose-100 text-rose-800 border-rose-200' },
  { value: 'future_prospect', label: 'Future Prospect', cls: 'bg-sky-100 text-sky-800 border-sky-200' },
];

const CREATED_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'custom', label: 'Custom range' },
];

const SORT_OPTIONS = [
  { value: 'enrolled_at', label: 'Enrolment date' },
  { value: 'created_at', label: 'Renewal created date' },
  { value: 'follow_up_date', label: 'Follow-up date' },
];

const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const RenewalsPanel = ({ onOpenLead, onSendRenewalLink, isSuperAdmin }) => {
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState([]);
  const [created, setCreated] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [q, setQ] = useState('');
  const [pendingFollowUp, setPendingFollowUp] = useState({});
  const [syncing, setSyncing] = useState(false);

  const params = useMemo(() => {
    const p = { sort_by: sortBy, sort_dir: sortDir, created };
    if (statuses.length) p.status = statuses.join(',');
    if (created === 'custom') {
      if (customFrom) p.created_from = customFrom;
      if (customTo) p.created_to = customTo;
    }
    if (q.trim()) p.q = q.trim();
    return p;
  }, [statuses, created, customFrom, customTo, sortBy, sortDir, q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/renewals`, { params, withCredentials: true });
      setData({ rows: res.data.rows || [], summary: res.data.summary || {} });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load renewals');
    } finally {
      setLoading(false);
    }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const save = async (id, payload) => {
    try {
      await axios.put(`${API_URL}/api/renewals/${id}`, payload, { withCredentials: true });
      toast.success('Renewal updated');
      setPendingFollowUp((p) => { const n = { ...p }; delete n[id]; return n; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const onStatusChange = (row, status) => {
    if (status === 'future_prospect') {
      setPendingFollowUp((p) => ({ ...p, [row.renewal_lead_id]: row.follow_up_date || '' }));
      return;
    }
    save(row.renewal_lead_id, { status });
  };

  const toggleStatus = (v) =>
    setStatuses((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data: res } = await axios.post(`${API_URL}/api/renewals/sync`, {}, { withCredentials: true });
      toast.success(`${res.stats.created} new renewal lead${res.stats.created === 1 ? '' : 's'} created`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const s = data.summary;

  return (
    <div className="card" data-testid="renewals-panel">
      <div className="card-header flex flex-wrap items-center justify-between gap-3 py-4">
        <div>
          <h2 className="font-display text-base md:text-lg font-semibold">Postnatal renewals</h2>
          <p className="text-xs text-muted-foreground">
            Every customer who has touched her 32nd week or delivered, plus postnatal access ending in ≤30 days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email…"
            className="input-base text-sm py-1.5 w-56" data-testid="renewals-search" />
          {isSuperAdmin && (
            <button type="button" onClick={runSync} disabled={syncing} data-testid="renewals-sync"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60">
              <Zap className="w-3.5 h-3.5" /> {syncing ? 'Checking…' : 'Check now'}
            </button>
          )}
          <button type="button" onClick={load} data-testid="renewals-refresh"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Status counters double as the status filter */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 px-4 pt-4" data-testid="renewals-summary">
        {STATUS_OPTIONS.map((o) => (
          <button key={o.value} type="button" onClick={() => toggleStatus(o.value)}
            data-testid={`renewals-stat-${o.value}`}
            className={`rounded-lg border p-2.5 text-left transition-colors ${
              statuses.includes(o.value) ? `${o.cls} ring-2 ring-offset-1 ring-slate-300` : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
            <div className="text-[11px] text-slate-500">{o.label}</div>
            <div className="text-lg font-bold text-slate-900">{s[o.value] ?? 0}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3 px-4 py-4 border-b border-slate-100" data-testid="renewals-filters">
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Created</label>
          <select value={created} onChange={(e) => setCreated(e.target.value)} data-testid="renewals-created-filter"
            className="input-base text-sm py-1.5 w-40">
            {CREATED_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {created === 'custom' && (
          <>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">From</label>
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                className="input-base text-sm py-1.5" data-testid="renewals-created-from" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">To</label>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                className="input-base text-sm py-1.5" data-testid="renewals-created-to" />
            </div>
          </>
        )}
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Sort by</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="renewals-sort-by"
            className="input-base text-sm py-1.5 w-52">
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <button type="button" onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
          data-testid="renewals-sort-dir"
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">
          <ArrowUpDown className="w-3.5 h-3.5" /> {sortDir === 'desc' ? 'Latest first' : 'Earliest first'}
        </button>
        {(statuses.length > 0 || created !== 'all') && (
          <button type="button" onClick={() => { setStatuses([]); setCreated('all'); setCustomFrom(''); setCustomTo(''); }}
            data-testid="renewals-clear-filters"
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-200">
            <X className="w-3 h-3" /> Clear
          </button>
        )}
        <div className="flex-1 text-right text-xs text-slate-500" data-testid="renewals-count">
          {data.rows.length} of {s.total ?? 0} renewal leads
        </div>
      </div>

      {loading ? (
        <TableRowsSkeleton rows={6} cols={7} />
      ) : data.rows.length === 0 ? (
        <div className="p-6">
          <EmptyState icon={CalendarClock} title="No renewal leads yet"
            description="A customer appears here the day she touches her 32nd week, or when her postnatal access is ending." />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="renewals-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Contact</th>
                <th className="px-4 py-2.5">Country</th>
                <th className="px-4 py-2.5">Renewal created</th>
                <th className="px-4 py-2.5">Renewal status</th>
                <th className="px-4 py-2.5">Sales notes</th>
                <th className="px-4 py-2.5">Sales rep</th>
                <th className="px-4 py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.map((r) => (
                <tr key={r.renewal_lead_id} className="hover:bg-slate-50/70 cursor-pointer"
                  data-testid={`renewal-row-${r.renewal_lead_id}`}
                  onClick={(e) => {
                    if (e.target.closest('button,a,input,select,textarea,label')) return;
                    r.lead_id && onOpenLead?.(r.lead_id);
                  }}>
                  <td className="px-4 py-3">
                    <button type="button" onClick={() => r.lead_id && onOpenLead?.(r.lead_id)}
                      className="font-semibold text-indigo-600 hover:underline text-left"
                      data-testid={`renewal-open-lead-${r.renewal_lead_id}`}>
                      {r.lead_name || r.email || r.phone || 'Unknown'}
                    </button>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {r.cycle === 'postnatal_renewal' && (
                        <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700"
                          data-testid={`renewal-cycle-${r.renewal_lead_id}`}>Postnatal renewal</span>
                      )}
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600"
                        title={r.edd ? `EDD ${r.edd}` : ''}>{r.trigger_label}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    <div>{r.phone || '—'}</div>
                    <div className="text-slate-400">{r.email || ''}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{r.country || 'Unknown'}</td>
                  <td className="px-4 py-3 text-xs text-slate-600" data-testid={`renewal-created-${r.renewal_lead_id}`}>
                    {fmtDate(r.created_at)}
                    {r.enrolled_at && <div className="text-slate-400">Enrolled {fmtDate(r.enrolled_at)}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <select value={pendingFollowUp[r.renewal_lead_id] !== undefined ? 'future_prospect' : (r.status || 'wip')}
                      onChange={(e) => onStatusChange(r, e.target.value)}
                      data-testid={`renewal-status-${r.renewal_lead_id}`}
                      className={`rounded-lg border px-2 py-1 text-xs font-semibold ${
                        STATUS_OPTIONS.find((o) => o.value === (r.status || 'wip'))?.cls || 'bg-white border-slate-200'}`}>
                      {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {(pendingFollowUp[r.renewal_lead_id] !== undefined || r.status === 'future_prospect') && (
                      <div className="mt-1.5 flex items-center gap-1.5" data-testid={`renewal-followup-wrap-${r.renewal_lead_id}`}>
                        <input type="date"
                          value={pendingFollowUp[r.renewal_lead_id] ?? r.follow_up_date ?? ''}
                          onChange={(e) => setPendingFollowUp((p) => ({ ...p, [r.renewal_lead_id]: e.target.value }))}
                          className="rounded-lg border border-slate-200 px-2 py-1 text-xs"
                          data-testid={`renewal-followup-${r.renewal_lead_id}`} />
                        {pendingFollowUp[r.renewal_lead_id] && pendingFollowUp[r.renewal_lead_id] !== r.follow_up_date && (
                          <button type="button"
                            onClick={() => save(r.renewal_lead_id, { status: 'future_prospect', follow_up_date: pendingFollowUp[r.renewal_lead_id] })}
                            data-testid={`renewal-followup-save-${r.renewal_lead_id}`}
                            className="rounded-lg bg-sky-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-sky-700">Save</button>
                        )}
                        {pendingFollowUp[r.renewal_lead_id] !== undefined && r.status !== 'future_prospect' && (
                          <button type="button"
                            onClick={() => setPendingFollowUp((p) => { const n = { ...p }; delete n[r.renewal_lead_id]; return n; })}
                            data-testid={`renewal-followup-cancel-${r.renewal_lead_id}`}
                            className="text-[11px] font-semibold text-slate-500 hover:text-slate-700">Cancel</button>
                        )}
                      </div>
                    )}
                    {pendingFollowUp[r.renewal_lead_id] !== undefined && r.status !== 'future_prospect' && (
                      <div className="mt-1 text-[10px] text-sky-600">Pick a follow-up date to save Future Prospect</div>
                    )}
                    {r.status === 'future_prospect' && r.follow_up_date && (
                      <div className="mt-1 text-[10px] text-slate-500">Reminder 1 day before</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-[220px]">
                    <span className="line-clamp-2" title={r.sales_notes || ''}>{r.sales_notes || '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600" data-testid={`renewal-rep-${r.renewal_lead_id}`}>
                    {r.assigned_rep_name || 'Unassigned'}
                    {r.assignment_reason === 'round_robin' && (
                      <div className="text-[10px] text-amber-600">Round-robin</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => r.lead_id && onSendRenewalLink?.(r.lead_id)}
                      data-testid={`renewal-send-link-${r.renewal_lead_id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">
                      <Link2 className="w-3.5 h-3.5" /> Payment link
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RenewalsPanel;
